// 第三方组件二开，AI 生成注释，自行辨别参考
// @ts-nocheck
import { Coords, getElementCoords, Dims, isDefaultScrollingElement, getCombinedData, getElementDims } from "./dom";
import { getViewportStart, getViewportDims } from "./viewport";

export function getCurrentScrollOffset(root: Element): Coords {
  return {
    x: root.scrollLeft,
    y: root.scrollTop
  }
}

/**
 * 添加滚动偏移量
 *
 * @param root 根元素
 * @param coords 坐标对象
 * @returns 返回新的坐标对象，包含x和y轴上的偏移量
 */
export function addScrollOffset(root: Element, coords: Coords) {
  const curOffset: Coords = getCurrentScrollOffset(root);
  return {
    x: coords.x + curOffset.x,
    y: coords.y + curOffset.y
  }
}

/**
 * 根据给定的根元素和坐标，添加适当的偏移量。
 *
 * @param root 根元素
 * @param coords 坐标
 * @returns 返回调整后的坐标
 */
export function addAppropriateOffset(root: Element, coords: Coords) {
  if (!coords || !root) {
    return;
  }

  // if there's a custom root, then we need to offset by that root's positioning relative to the window
  // before adjusting for its scroll values
  if (!isDefaultScrollingElement(root)) {
    const rootCoords: Coords = getElementCoords(root);
    return addScrollOffset(root, {
      x: coords.x - rootCoords.x,
      y: coords.y - rootCoords.y
    })
  } else {
    return addScrollOffset(root, coords);
  }
}

// apply a common offset calculation where b is centered relative to a. If b is larger than a, the result is that a will be centered within b.
// b is the moveable element: the returned value will specify where to place b to achieve centering.
/**
 * 将给定的坐标加上中心偏移量，使其相对于另一维度（b）的中心点对齐
 *
 * @param aCoords 坐标对象，包含 x 和 y 属性
 * @param aDims 维度对象，包含 width 和 height 属性
 * @param b 维度对象，包含 width 和 height 属性
 * @returns 返回一个新的坐标对象，包含 x 和 y 属性，表示经过中心偏移后的位置
 */
export function applyCenterOffset(aCoords: Coords, aDims: Dims, b: Dims): Coords {
  return {
    x: aCoords.x + (aDims.width / 2) - (b.width / 2),
    y: aCoords.y + (aDims.height / 2) - (b.height / 2)
  }
}

/**
 * 将视口中心点移动到指定元素中心位置
 *
 * @param root 根元素
 * @param coords 目标元素坐标
 * @param dims 目标元素尺寸
 * @returns 返回调整后的视口坐标
 */
export function centerViewportAround(root: Element, coords: Coords, dims: Dims): Coords {
  return applyCenterOffset(coords, dims, getViewportDims(root));
}

/**
 * 将视口中心围绕指定的两个元素进行定位
 *
 * @param root 根元素
 * @param a 第一个元素
 * @param b 第二个元素
 * @param aPosition 第一个元素的坐标，可选
 * @param bPosition 第二个元素的坐标，可选
 * @returns 返回视口中心的坐标，如果参数不正确则返回undefined
 */
export function centerViewportAroundElements(root: Element, a: HTMLElement, b: HTMLElement, aPosition?: Coords, bPosition?: Coords): Coords | undefined {
  if (!root || !a || !b) {
    return;
  }

  const aCoords = aPosition || getElementCoords(a);
  const bCoords = bPosition || getElementCoords(b);
  const aDims = getElementDims(a);
  const bDims = getElementDims(b);
  const {coords, dims} = getCombinedData(aCoords, aDims, bCoords, bDims);
  return centerViewportAround(root, coords, dims);
}

// get the coordinates the viewport would need to be placed for the element to be centered
/**
 * 将视口中心对准指定元素
 *
 * @param root 根元素
 * @param element 要对准的HTML元素
 * @returns 返回对齐后的坐标
 */
export function centerViewportAroundElement(root: Element, element: HTMLElement): Coords {
  const elementDims: Dims = getElementDims(element);
  const elementCoords: Coords = getElementCoords(element);

  return centerViewportAround(root, elementCoords, elementDims);
}

// get the center coord of the viewport. If element is provided, the return value is the origin 
// which would align that element's center with the viewport center. If atViewportPosition is provided,
// gets the viewport's center at that position
/**
 * 获取视口中心点坐标
 *
 * @param root 根元素
 * @param element 可选参数，要获取中心点的元素
 * @param atViewportPosition 可选参数，指定视口位置
 * @returns 返回中心点坐标，若未指定root，则返回undefined
 */
export function getViewportCenter(root: Element, element?: HTMLElement, atViewportPosition?: Coords): Coords | undefined {
  if (!root) {
    return;
  }
  const startCoords: Coords = atViewportPosition || getViewportStart(root);
  const viewportDims: Dims = getViewportDims(root);
  const elementDims: Dims = getElementDims(element) ?? { height: 0, width: 0 }

  return applyCenterOffset(startCoords, viewportDims, elementDims);
}
